import type { CubeQuery } from "@models/ISchema";
import type { AnnualSchedulePeriod, AnnualScheduleWeek } from "../types";
import { compareIds, isPresent } from "./shared";

/**
 * Builds the horizontal axis from the enabled week catalog.
 *
 * @param weeks - Catalog weeks, including weeks with no scheduled blocks.
 * @param period - Selected period; null includes the entire catalog.
 * @returns Chronological weeks that overlap the period, retaining their catalog numbers.
 *
 * @remarks The catalog position matches the week number used by the weekly calendar.
 * A week at a period boundary remains visible in full.
 */
export const getAnnualWeeks = (
  weeks: CubeQuery["editorWeeks"],
  period: AnnualSchedulePeriod,
): AnnualScheduleWeek[] => {
  const numberedWeeks = (weeks ?? []).map((week, index) =>
    week ? { ...week, catalogNumber: index + 1 } : null,
  );
  const uniqueWeeks = new Map(numberedWeeks.filter(isPresent).map(week => [week.id, week]));

  const visibleWeeks = [...uniqueWeeks.values()].filter(week => {
    if (!period) return true;
    if (!period.start || !period.end) return false;

    const startsBeforePeriodEnds = week.startingDate?.slice(0, 10) <= period.end.slice(0, 10);
    const endsAfterPeriodStarts = week.endingDate?.slice(0, 10) >= period.start.slice(0, 10);

    return startsBeforePeriodEnds && endsAfterPeriodStarts;
  });

  return visibleWeeks.sort(
    (a, b) => (a.startingDate ?? "").localeCompare(b.startingDate ?? "") || compareIds(a.id, b.id),
  );
};
